import { APP_VERSION } from "../appVersion";
import {
  DEFAULT_VISUAL_LAB_PARAMETERS_FROM_CATALOG,
  VISION_RUNTIME_PARAMETER_CATALOG_SHA256,
  VISION_RUNTIME_PARAMETER_CATALOG_VERSION,
} from "../generated/visionRuntimeParameters";
import {
  VISION_PIPELINE_FINGERPRINT_VERSION,
  VISION_PRODUCTION_COMPONENT_SHA256,
  VISION_PRODUCTION_PIPELINE_SHA256,
} from "../generated/visionPipelineIntegrity";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { VECTOR_TEMPLATE_OCR_REGIONS } from "../generated/vectorTemplateContract";
import { ensureProductionCnnBundleCached } from "./cnnWebInferenceService";
export type VisualLabStage = "experiment" | "test" | "production";

export type VisualLabOcrRegion = {
  field: "materialText" | "materialType" | "materialNumber" | "storageAddress";
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
  mode: "line" | "block";
  whitelist?: string | null;
};

export type VisualLabProfile = {
  id: number;
  name: string;
  description: string | null;
  stage: VisualLabStage;
  is_active: boolean;
  detection_parameters: Record<string, number>;
  ocr_regions: VisualLabOcrRegion[];
  created_at: string;
  updated_at: string;
  activated_at: string | null;
  change_reason: string | null;
  release_notes: string | null;
  quality_baseline: Record<string, number> | null;
};


export type VisionProductionBundle = {
  schema_version: number;
  bundle_version: string;
  bundle_id: string;
  recognition_revision?: number | null;
  candidate_id: string;
  candidate_kind: "end_to_end";
  activated_at: string;
  runtime_ready: boolean;
  runtime_api?: string | null;
  release_ready?: boolean;
  model_url: string;
  candidate_config_sha256?: string | null;
  candidate_config?: Record<string, unknown>;
  validation?: Record<string, unknown>;
  promotion_gates?: Record<string, unknown>;
  compatibility?: {
    schema_version?: number | null;
    package_version?: string | null;
    runtime_api?: string | null;
    minimum_app_version?: string | null;
    server_contract_version?: string | null;
    client_contract_version?: string | null;
    model_bundle_contract_version?: string | null;
    engine_catalog_version?: string | null;
    atomic_installation_contract?: string | null;
    candidate_config_version?: string | null;
    runtime_parity_version?: string | null;
    requires?: string[];
    capabilities?: string[];
  };
  model_bundle_contract_sha256?: string | null;
  model_bundle_contract?: Record<string, unknown>;
  engine_manifest?: Record<string, unknown>;
  integrity?: {
    algorithm?: string | null;
    candidate_config_sha256?: string | null;
    bundle_payload_sha256?: string | null;
    verified_at?: string | null;
  };
  provenance?: Record<string, unknown>;
  cnn: {
    model_id: string;
    architecture?: string | null;
    dataset_version?: string | null;
    validation_corner_error?: number | null;
    worker_model_storage_key?: string | null;
    web_model_storage_key?: string | null;
    model_sha256?: string | null;
    web_model_sha256?: string | null;
  };
  hybrid: { params: Record<string, number>; input_scale: number; confidence_threshold: number; fast_path_enabled: boolean };
  ocr: { params: Record<string, number>; regions?: VisualLabOcrRegion[] };
  extraction?: Record<string, unknown>;
  runtime?: Record<string, unknown>;
};

export type ActiveVisualLabProfile = {
  profile: VisualLabProfile | null;
  source: "database" | "default";
  detection_parameters: Record<string, number>;
  ocr_regions: VisualLabOcrRegion[];
  production_bundle?: VisionProductionBundle | null;
};

export const CURRENT_VISION_RUNTIME_API = "vision-runtime-v6";
export const SUPPORTED_VISION_RUNTIME_APIS = ["vision-runtime-v3", "vision-runtime-v4", "vision-runtime-v5", CURRENT_VISION_RUNTIME_API] as const;

export type VisionRuntimeSnapshot = {
  schemaVersion: 1;
  capturedAt: string;
  source: "database" | "default";
  profileId: number | null;
  profileName: string | null;
  profileStage: VisualLabStage | null;
  profileUpdatedAt: string | null;
  profileActivatedAt: string | null;
  runtimeApi: string | null;
  runtimeCompatible: boolean;
  runtimeCompatibilityReason: string;
  bundleId: string | null;
  bundleVersion: string | null;
  recognitionRevision: number | null;
  bundlePayloadSha256: string | null;
  candidateConfigSha256: string | null;
  runtimeParameterCatalogVersion: string;
  runtimeParameterCatalogSha256: string;
  pipelineFingerprintVersion: string;
  productionPipelineSha256: string;
  detectionParameters: Record<string, number>;
  cnn: {
    enabled: boolean;
    modelId: string | null;
    modelSha256: string | null;
    webModelSha256: string | null;
    runtime: string | null;
    inputTransform: string | null;
  };
  hybrid: {
    params: Record<string, number>;
    inputScale: number;
    confidenceThreshold: number;
    fastPathEnabled: boolean;
  };
  ocr: {
    source: "bundle" | "profile" | "default";
    params: Record<string, number>;
    regions: VisualLabOcrRegion[];
    runtime: string;
    validationRuntime: string | null;
    preprocessRuntime: string | null;
    canonicalPreprocess: boolean;
    smartGridMultiPass: boolean;
    languageAssetSha256: {
      deu: string | null;
      eng: string | null;
    };
  };
  productionBundle: VisionProductionBundle | null;
};

export type VisionRuntimeProvenance = Omit<VisionRuntimeSnapshot, "productionBundle"> & {
  fastPathAttempted?: boolean;
  fastPathFallbackUsed?: boolean;
  cnnInference?: {
    confidence: number;
    perImageUncertainty: number | null;
    ttaMaxCornerDelta: number | null;
    ttaPasses: number;
    expectedCornerError: number | null;
    calibrationVersion: string | null;
    calibrationSource: string | null;
    geometryScore: number;
    runtimeMs: number;
  } | null;
};

export const DEFAULT_VISUAL_LAB_PARAMETERS: Record<string, number> = { ...DEFAULT_VISUAL_LAB_PARAMETERS_FROM_CATALOG };

export const DEFAULT_VISUAL_LAB_OCR_REGIONS: VisualLabOcrRegion[] = [
  { field: "materialText", label: "Materialkurztext", ...VECTOR_TEMPLATE_OCR_REGIONS.materialText, mode: "block" },
  { field: "materialType", label: "Typ", ...VECTOR_TEMPLATE_OCR_REGIONS.materialType, mode: "line", whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789ÄÖÜäöüß-_/.,+() " },
  { field: "materialNumber", label: "Materialnummer", ...VECTOR_TEMPLATE_OCR_REGIONS.materialNumber, mode: "line", whitelist: "0123456789" },
  { field: "storageAddress", label: "Lagerort", ...VECTOR_TEMPLATE_OCR_REGIONS.storageAddress, mode: "block", whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 " },
];

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetchWithTimeout(url, {
    credentials: "include",
    cache: "no-store",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  }, 1500);
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json() as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch { /* ignore */ }
    throw new Error(detail);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

const ACTIVE_CACHE_KEY = "materialentnahme:visual-lab:active-profile";
const ACTIVE_CACHE_AT_KEY = "materialentnahme:visual-lab:active-profile-at";
const MODEL_BUNDLE_INSTALL_RECEIPT_PREFIX = "materialentnahme:vision:model-bundle-install:v1:";
const MODEL_BUNDLE_BROWSER_INSTALL_CONTRACT = "browser-model-bundle-install-v1";
let activeCache: { at: number; value: ActiveVisualLabProfile } | null = null;

export type VisionProductionRuntimeDisplay = {
  mode: "bundle" | "profile";
  bundleId: string | null;
  bundleVersion: string | null;
  recognitionRevision: number | null;
  label: string;
  freshness: "current" | "cached" | "offline";
  checkedAt: number | null;
};

export const VISION_PRODUCTION_UPDATED_EVENT = "materialentnahme:vision-production-updated";

export type VisionRecognitionServerStatus = {
  mode: "optimized" | "standard";
  recognition_revision: number | null;
  bundle_id: string | null;
  bundle_version: string | null;
  activated_at: string | null;
  profile_id: number | null;
  profile_name: string | null;
  integrity_status: string | null;
  integrity_checked_at: string | null;
  integrity_reasons: string[];
  fallback_active: boolean;
};

export async function fetchVisionRecognitionServerStatus(): Promise<VisionRecognitionServerStatus> {
  return api<VisionRecognitionServerStatus>("/api/visual-lab/recognition-status");
}

function completeVisualLabParameters(parameters: Record<string, number> | null | undefined): Record<string, number> {
  return {
    ...DEFAULT_VISUAL_LAB_PARAMETERS,
    ...(parameters ?? {}),
  };
}

function normalizeActiveVisualLabProfile(value: ActiveVisualLabProfile): ActiveVisualLabProfile {
  const detectionParameters = completeVisualLabParameters(value.detection_parameters);
  return {
    ...value,
    detection_parameters: detectionParameters,
    profile: value.profile
      ? { ...value.profile, detection_parameters: completeVisualLabParameters(value.profile.detection_parameters) }
      : null,
    ocr_regions: cloneOcrRegions(value.ocr_regions ?? DEFAULT_VISUAL_LAB_OCR_REGIONS),
    production_bundle: cloneProductionBundle(value.production_bundle),
  };
}

function defaultActiveVisualLabProfile(): ActiveVisualLabProfile {
  return {
    profile: null,
    source: "default",
    detection_parameters: { ...DEFAULT_VISUAL_LAB_PARAMETERS },
    ocr_regions: DEFAULT_VISUAL_LAB_OCR_REGIONS.map((region) => ({ ...region })),
    production_bundle: null,
  };
}

export function readCachedActiveVisualLabProfile(): ActiveVisualLabProfile | null {
  if (activeCache) return activeCache.value;
  try {
    const cached = localStorage.getItem(ACTIVE_CACHE_KEY);
    if (!cached) return null;
    const parsed = normalizeActiveVisualLabProfile(JSON.parse(cached) as ActiveVisualLabProfile);
    const storedAt = Number(localStorage.getItem(ACTIVE_CACHE_AT_KEY) ?? 0);
    activeCache = { at: Number.isFinite(storedAt) && storedAt > 0 ? storedAt : 0, value: parsed };
    return parsed;
  } catch {
    return null;
  }
}

async function refreshActiveVisualLabProfileFromServer(expectedBundleId?: string | null): Promise<ActiveVisualLabProfile> {
  const value = normalizeActiveVisualLabProfile(await api<ActiveVisualLabProfile>("/api/visual-lab/active-profile"));
  const actualBundleId = value.production_bundle?.bundle_id ?? null;
  if (expectedBundleId !== undefined && actualBundleId !== expectedBundleId) {
    throw new Error("Server-Production entspricht noch nicht dem erwarteten Bundle-Pointer.");
  }
  if (value.production_bundle?.bundle_version === "production-bundle-v5-atomic-local") {
    await installBrowserModelBundle(value.production_bundle);
  }
  const at = Date.now();
  if (typeof window !== "undefined") {
    try {
      // Der vollständige Browser-Bundle-Install läuft vorher. Dieser einzelne setItem ist
      // der lokale Active-Pointer-Commit; bis dahin bleibt die vorige Production erhalten.
      window.localStorage.setItem(ACTIVE_CACHE_AT_KEY, String(at));
      window.localStorage.setItem(ACTIVE_CACHE_KEY, JSON.stringify(value));
    } catch (error) {
      if (value.production_bundle?.bundle_version === "production-bundle-v5-atomic-local") {
        throw new Error("Model Bundle ist vorbereitet, aber der lokale Active-Pointer konnte nicht atomar committed werden.", { cause: error });
      }
    }
    window.dispatchEvent(new CustomEvent(VISION_PRODUCTION_UPDATED_EVENT, { detail: value }));
  }
  activeCache = { at, value };
  return value;
}

function cloneOcrRegions(regions: readonly VisualLabOcrRegion[]): VisualLabOcrRegion[] {
  return regions.map((region) => ({ ...region }));
}

function cloneProductionBundle(bundle: VisionProductionBundle | null | undefined): VisionProductionBundle | null {
  if (!bundle) return null;
  return JSON.parse(JSON.stringify(bundle)) as VisionProductionBundle;
}

function runtimeApiForBundle(bundle: VisionProductionBundle | null | undefined): string | null {
  return bundle?.runtime_api ?? bundle?.compatibility?.runtime_api ?? null;
}

function isSupportedRuntimeApi(runtimeApi: string | null): boolean {
  return runtimeApi !== null && (SUPPORTED_VISION_RUNTIME_APIS as readonly string[]).includes(runtimeApi);
}

function bundleOwnsOcrRuntime(bundle: VisionProductionBundle | null | undefined, runtimeCompatible: boolean): boolean {
  if (!bundle || !runtimeCompatible || bundle.runtime_ready !== true) return false;
  if (bundle.bundle_version === "production-bundle-v2-candidate-integrity") return true;
  if (bundle.bundle_version === "production-bundle-v3-runtime-parity") return true;
  return bundle.bundle_version === "production-bundle-v4-release-ready" || bundle.bundle_version === "production-bundle-v5-atomic-local";
}

function bundleOcrPreprocessRuntime(bundle: VisionProductionBundle | null | undefined): string | null {
  if (!bundle) return null;
  const runtime = bundle.runtime ?? {};
  const candidateOcr = (bundle.candidate_config?.ocr ?? {}) as Record<string, unknown>;
  return String(candidateOcr.preprocess_runtime ?? runtime.ocr_preprocess ?? "") || null;
}

function bundleUsesCanonicalOcr(bundle: VisionProductionBundle | null | undefined, runtimeCompatible: boolean): boolean {
  if (!bundleOwnsOcrRuntime(bundle, runtimeCompatible)) return false;
  if (bundle?.bundle_version === "production-bundle-v3-runtime-parity") return true;
  if (bundle?.bundle_version !== "production-bundle-v4-release-ready" && bundle?.bundle_version !== "production-bundle-v5-atomic-local") return false;
  const preprocessRuntime = bundleOcrPreprocessRuntime(bundle);
  return preprocessRuntime === "canonical-ocr-js-v1"
    || preprocessRuntime === "canonical-ocr-js-v2-smart-grid-multipass";
}

function bundleUsesSmartOcr(bundle: VisionProductionBundle | null | undefined, runtimeCompatible: boolean): boolean {
  if (!bundleOwnsOcrRuntime(bundle, runtimeCompatible)) return false;
  return bundleOcrPreprocessRuntime(bundle) === "canonical-ocr-js-v2-smart-grid-multipass";
}

type BrowserModelBundleInstallReceipt = {
  schema_version: 1;
  contract_version: typeof MODEL_BUNDLE_BROWSER_INSTALL_CONTRACT;
  bundle_id: string;
  bundle_payload_sha256: string;
  model_bundle_contract_sha256: string;
  engine_manifest_sha256: string;
  cnn_web_model_sha256: string | null;
  installed_with_app_version: string;
  installed_at: string;
};

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map((item) => stableJson(item)).join(",")}]`;
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

async function sha256Text(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("Der Browser unterstützt keine SHA-256-Prüfung für Model Bundles.");
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function modelBundleInstallReceiptKey(bundleId: string): string {
  return `${MODEL_BUNDLE_INSTALL_RECEIPT_PREFIX}${bundleId}`;
}

function readBrowserModelBundleInstallReceipt(bundleId: string): BrowserModelBundleInstallReceipt | null {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(modelBundleInstallReceiptKey(bundleId)) ?? "null") as BrowserModelBundleInstallReceipt | null;
    return parsed && parsed.schema_version === 1 && parsed.contract_version === MODEL_BUNDLE_BROWSER_INSTALL_CONTRACT ? parsed : null;
  } catch { return null; }
}

function browserModelBundleInstallationMatches(bundle: VisionProductionBundle): boolean {
  if (bundle.bundle_version !== "production-bundle-v5-atomic-local") return true;
  const receipt = readBrowserModelBundleInstallReceipt(bundle.bundle_id);
  const payloadSha = String(bundle.integrity?.bundle_payload_sha256 ?? "");
  const contractSha = String(bundle.model_bundle_contract_sha256 ?? "");
  const contract = (bundle.model_bundle_contract ?? {}) as Record<string, unknown>;
  const engineSha = String(contract.engine_manifest_sha256 ?? "");
  const webModelSha = bundle.cnn?.model_id ? String(bundle.cnn.web_model_sha256 ?? "") : null;
  return Boolean(receipt
    && receipt.bundle_id === bundle.bundle_id
    && payloadSha && receipt.bundle_payload_sha256 === payloadSha
    && contractSha && receipt.model_bundle_contract_sha256 === contractSha
    && engineSha && receipt.engine_manifest_sha256 === engineSha
    && receipt.cnn_web_model_sha256 === webModelSha);
}

async function installBrowserModelBundle(bundle: VisionProductionBundle): Promise<void> {
  if (bundle.bundle_version !== "production-bundle-v5-atomic-local") return;
  const compatibility = visionRuntimeCompatibility(bundle, { requireLocalInstallation: false });
  if (!compatibility.compatible) throw new Error(compatibility.reason);
  if (typeof window === "undefined") throw new Error("Model Bundle kann außerhalb des Browsers nicht lokal installiert werden.");
  const payloadSha = String(bundle.integrity?.bundle_payload_sha256 ?? "");
  const expectedContractSha = String(bundle.model_bundle_contract_sha256 ?? "");
  const contract = (bundle.model_bundle_contract ?? {}) as Record<string, unknown>;
  const engineManifest = (bundle.engine_manifest ?? {}) as Record<string, unknown>;
  const expectedEngineSha = String(contract.engine_manifest_sha256 ?? "");
  if (!/^[a-f0-9]{64}$/.test(payloadSha) || !/^[a-f0-9]{64}$/.test(expectedContractSha) || !/^[a-f0-9]{64}$/.test(expectedEngineSha)) {
    throw new Error("Model Bundle besitzt keine vollständigen SHA-256-Fingerprints.");
  }
  const payload = { ...bundle } as Record<string, unknown>;
  delete payload.integrity;
  if (await sha256Text(stableJson(payload)) !== payloadSha) throw new Error("Model-Bundle-Payload-Fingerprint stimmt lokal nicht.");
  const contractSha = await sha256Text(stableJson(contract));
  if (contractSha !== expectedContractSha) throw new Error("Model-Bundle-Contract-Fingerprint stimmt lokal nicht.");
  const engineSha = await sha256Text(stableJson(engineManifest));
  if (engineSha !== expectedEngineSha) throw new Error("Engine-Manifest-Fingerprint stimmt lokal nicht.");
  if (!browserEngineManifestAllowed(engineManifest) || stableJson(engineManifest) !== stableJson(expectedEngineManifest(bundle))) {
    throw new Error("Engine-Manifest ist lokal nicht allowlisted oder stimmt nicht mit der Bundle-Runtime überein.");
  }
  await ensureProductionCnnBundleCached(bundle);
  const receipt: BrowserModelBundleInstallReceipt = {
    schema_version: 1,
    contract_version: MODEL_BUNDLE_BROWSER_INSTALL_CONTRACT,
    bundle_id: bundle.bundle_id,
    bundle_payload_sha256: payloadSha,
    model_bundle_contract_sha256: expectedContractSha,
    engine_manifest_sha256: expectedEngineSha,
    cnn_web_model_sha256: bundle.cnn?.model_id ? String(bundle.cnn.web_model_sha256 ?? "") : null,
    installed_with_app_version: APP_VERSION,
    installed_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(modelBundleInstallReceiptKey(bundle.bundle_id), JSON.stringify(receipt));
  } catch {
    throw new Error("Model Bundle konnte lokal nicht atomar committed werden.");
  }
}

function parseAppSemver(value: string): [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(value.trim());
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}

function appVersionAtLeast(current: string, minimum: string): boolean {
  const a = parseAppSemver(current), b = parseAppSemver(minimum);
  if (!a || !b) return false;
  if (a[0] !== b[0]) return a[0] > b[0];
  if (a[1] !== b[1]) return a[1] > b[1];
  return a[2] >= b[2];
}

const VISION_BROWSER_BUNDLE_CONTRACT = "vision-browser-bundle-v1";
const VISION_SERVER_BUNDLE_CONTRACT = "vision-server-bundle-v1";
const VISION_MODEL_BUNDLE_CONTRACT = "vision-model-bundle-contract-v1";
const VISION_MODEL_BUNDLE_PACKAGE = "vision-production-package-v2-atomic-install";
const VISION_ENGINE_CATALOG = "vision-engine-catalog-v1";
const VISION_ATOMIC_INSTALL_CONTRACT = "atomic-local-install-v1";

function expectedEngineManifest(bundle: VisionProductionBundle): Record<string, unknown> {
  const config = (bundle.candidate_config ?? {}) as Record<string, unknown>;
  const candidateOcr = (config.ocr ?? {}) as Record<string, unknown>;
  const candidateCnn = (config.cnn ?? {}) as Record<string, unknown>;
  const extraction = (config.extraction ?? {}) as Record<string, unknown>;
  const decoder = (extraction.storage_address_decoder ?? {}) as Record<string, unknown>;
  const cnn = (bundle.cnn ?? {}) as unknown as Record<string, unknown>;
  const hasCnn = Object.keys(cnn).length > 0 || Boolean(candidateCnn.model_id);
  return {
    schema_version: 1,
    catalog_version: VISION_ENGINE_CATALOG,
    detection: "opencv-js-dedicated-worker",
    ocr: String(candidateOcr.runtime ?? "tesseract.js-browser-v7-local-offline-v3-deterministic"),
    cnn: hasCnn ? String(cnn.runtime ?? candidateCnn.runtime ?? "disabled") : "disabled",
    storage_decoder: String(decoder.contract_version ?? "none"),
    inference_backend: "local-browser-offline",
  };
}

function browserEngineManifestAllowed(manifest: Record<string, unknown>): boolean {
  return manifest.catalog_version === VISION_ENGINE_CATALOG
    && manifest.detection === "opencv-js-dedicated-worker"
    && manifest.ocr === "tesseract.js-browser-v7-local-offline-v3-deterministic"
    && ["disabled", "tiny-corner-cnn-js-v3-tta-uncertainty"].includes(String(manifest.cnn ?? ""))
    && ["none", "storage-address-four-subcell-decoder-v1"].includes(String(manifest.storage_decoder ?? ""))
    && manifest.inference_backend === "local-browser-offline";
}

export function visionRuntimeCompatibility(
  bundle: VisionProductionBundle | null | undefined,
  options: { requireLocalInstallation?: boolean } = {},
): { compatible: boolean; reason: string; runtimeApi: string | null } {
  if (!bundle) return { compatible: true, reason: "Kein Production-Bundle aktiv; Profil-/Default-Runtime wird verwendet.", runtimeApi: null };
  const runtimeApi = runtimeApiForBundle(bundle);
  if (bundle.bundle_version === "production-bundle-v2-candidate-integrity" || bundle.bundle_version === "production-bundle-v3-runtime-parity") {
    return { compatible: true, reason: "Legacy-Bundle mit explizit unterstützter Runtime-Semantik.", runtimeApi };
  }
  if (bundle.bundle_version === "production-bundle-v5-atomic-local") {
    const compatibility = bundle.compatibility ?? {};
    const contract = (bundle.model_bundle_contract ?? {}) as Record<string, unknown>;
    const minimum = String(compatibility.minimum_app_version ?? contract.minimum_app_version ?? "");
    if (!minimum || !appVersionAtLeast(APP_VERSION, minimum)) {
      return { compatible: false, reason: `Model Bundle benötigt mindestens PWA v${minimum || "?"}; lokal läuft v${APP_VERSION}.`, runtimeApi };
    }
    if (compatibility.schema_version !== 2 || compatibility.package_version !== VISION_MODEL_BUNDLE_PACKAGE) {
      return { compatible: false, reason: "Model-Bundle-Paketvertrag wird von dieser PWA nicht unterstützt.", runtimeApi };
    }
    if (compatibility.client_contract_version !== VISION_BROWSER_BUNDLE_CONTRACT || contract.client_contract_version !== VISION_BROWSER_BUNDLE_CONTRACT) {
      return { compatible: false, reason: "Model Bundle besitzt einen inkompatiblen Browser-Client-Vertrag.", runtimeApi };
    }
    if (compatibility.server_contract_version !== VISION_SERVER_BUNDLE_CONTRACT || contract.server_contract_version !== VISION_SERVER_BUNDLE_CONTRACT) {
      return { compatible: false, reason: "Model Bundle besitzt einen inkompatiblen Server-Vertrag.", runtimeApi };
    }
    if (compatibility.model_bundle_contract_version !== VISION_MODEL_BUNDLE_CONTRACT || contract.contract_version !== VISION_MODEL_BUNDLE_CONTRACT || contract.bundle_schema_version !== 5) {
      return { compatible: false, reason: "Model-Bundle-Schema wird von dieser PWA nicht unterstützt.", runtimeApi };
    }
    if (compatibility.engine_catalog_version !== VISION_ENGINE_CATALOG || contract.engine_catalog_version !== VISION_ENGINE_CATALOG || compatibility.atomic_installation_contract !== VISION_ATOMIC_INSTALL_CONTRACT || contract.atomic_installation_contract !== VISION_ATOMIC_INSTALL_CONTRACT) {
      return { compatible: false, reason: "Model Bundle besitzt einen inkompatiblen Engine-/Installationsvertrag.", runtimeApi };
    }
    if (compatibility.runtime_api !== runtimeApi || contract.runtime_api !== runtimeApi) {
      return { compatible: false, reason: "Model Bundle besitzt inkonsistente Runtime-API-Deklarationen.", runtimeApi };
    }
    const engineManifest = (bundle.engine_manifest ?? {}) as Record<string, unknown>;
    if (!browserEngineManifestAllowed(engineManifest) || stableJson(engineManifest) !== stableJson(expectedEngineManifest(bundle))) {
      return { compatible: false, reason: "Model Bundle besitzt eine nicht allowlistete oder inkonsistente Engine-Runtime.", runtimeApi };
    }
    const pipelineFingerprint = (contract.pipeline_fingerprint ?? {}) as Record<string, unknown>;
    if (pipelineFingerprint.version !== VISION_PIPELINE_FINGERPRINT_VERSION || pipelineFingerprint.production_sha256 !== VISION_PRODUCTION_PIPELINE_SHA256) {
      return { compatible: false, reason: "Model Bundle wurde gegen eine andere Production-Pipeline installiert; sicherer Profil-/OpenCV-Fallback ist aktiv.", runtimeApi };
    }
    if (options.requireLocalInstallation !== false && !browserModelBundleInstallationMatches(bundle)) {
      return { compatible: false, reason: "Model Bundle ist auf diesem Gerät noch nicht vollständig lokal committed; bisheriger/fallback Erkennungsstand bleibt aktiv.", runtimeApi };
    }
  }
  if (!runtimeApi) return { compatible: false, reason: "Production-Bundle besitzt keine Runtime-API.", runtimeApi: null };
  if (!isSupportedRuntimeApi(runtimeApi)) return { compatible: false, reason: `Runtime-API ${runtimeApi} wird von dieser PWA nicht unterstützt.`, runtimeApi };
  const candidateConfigVersion = String(bundle.candidate_config?.config_version ?? "");
  if (["vision-candidate-config-v10-pipeline-fingerprint", "vision-candidate-config-v11-browser-ocr-parity", "vision-candidate-config-v12-equal-field-contract", "vision-candidate-config-v13-learning-contract"].includes(candidateConfigVersion)) {
    const pipelineFingerprint = (bundle.candidate_config?.pipeline_fingerprint ?? {}) as Record<string, unknown>;
    if (
      pipelineFingerprint.version !== VISION_PIPELINE_FINGERPRINT_VERSION
      || pipelineFingerprint.production_sha256 !== VISION_PRODUCTION_PIPELINE_SHA256
    ) {
      return { compatible: false, reason: "Production-Bundle wurde gegen eine andere Vision-Pipeline validiert; sicherer Profil-/OpenCV-Fallback ist aktiv.", runtimeApi };
    }
  }
  return { compatible: true, reason: `Runtime-API ${runtimeApi} wird unterstützt.`, runtimeApi };
}

export function visionProductionRuntimeDisplay(active: ActiveVisualLabProfile): VisionProductionRuntimeDisplay {
  const bundle = active.production_bundle ?? null;
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  const checkedAt = activeCache?.at && activeCache.at > 0 ? activeCache.at : null;
  const age = checkedAt === null ? Number.POSITIVE_INFINITY : Math.max(0, Date.now() - checkedAt);
  const freshness: VisionProductionRuntimeDisplay["freshness"] = !online ? "offline" : age <= 30_000 ? "current" : "cached";
  if (bundle) {
    const revision = Number.isInteger(bundle.recognition_revision) && Number(bundle.recognition_revision) > 0
      ? Number(bundle.recognition_revision)
      : null;
    return {
      mode: "bundle", bundleId: bundle.bundle_id ?? null, bundleVersion: bundle.bundle_version ?? null,
      recognitionRevision: revision,
      label: revision ? `Etikettenerkennung · Stand ${revision}` : "Etikettenerkennung · Optimierter Stand", freshness, checkedAt,
    };
  }
  return {
    mode: "profile", bundleId: null, bundleVersion: null, recognitionRevision: null,
    label: "Etikettenerkennung · Standard", freshness, checkedAt,
  };
}

export async function getActiveVisualLabProfileBoundedFresh(maxWaitMs = 400, staleAfterMs = 10_000): Promise<ActiveVisualLabProfile> {
  const cached = readCachedActiveVisualLabProfile();
  if (typeof navigator !== "undefined" && navigator.onLine === false) return cached ?? defaultActiveVisualLabProfile();
  const age = activeCache?.at ? Math.max(0, Date.now() - activeCache.at) : Number.POSITIVE_INFINITY;
  if (cached && age <= staleAfterMs) return cached;
  const refresh = refreshActiveVisualLabProfileFromServer();
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<null>((resolve) => { timeoutHandle = setTimeout(() => resolve(null), Math.max(0, maxWaitMs)); });
  try {
    const value = await Promise.race([refresh, timeout]);
    if (value) return value;
    void refresh.catch(() => undefined);
    return cached ?? defaultActiveVisualLabProfile();
  } catch {
    return cached ?? defaultActiveVisualLabProfile();
  } finally {
    if (timeoutHandle !== null) clearTimeout(timeoutHandle);
  }
}

export async function createVisionRuntimeSnapshot(options: { force?: boolean; boundedFreshnessMs?: number; staleAfterMs?: number } = {}): Promise<VisionRuntimeSnapshot> {
  const active = options.boundedFreshnessMs !== undefined
    ? await getActiveVisualLabProfileBoundedFresh(options.boundedFreshnessMs, options.staleAfterMs ?? 10_000)
    : await getActiveVisualLabProfile(options.force === true);
  // Deep clone: der Snapshot besitzt seine eigene Bundle-Kopie und kann durch einen
  // späteren Background-Refresh des Active-Profile-Caches nicht verändert werden.
  const bundle = cloneProductionBundle(active.production_bundle);
  const compatibility = visionRuntimeCompatibility(bundle);
  const canUseBundleRuntime = Boolean(bundle?.runtime_ready) && compatibility.compatible;
  const ownsOcr = bundleOwnsOcrRuntime(bundle, compatibility.compatible);
  // Without a Production Bundle, use the platform-independent canonical single-pass
  // preprocessing. Legacy bundles keep their historic semantics; current v4 bundles
  // explicitly declare whether canonical/smart OCR is owned by the bundle.
  const canonicalOcr = bundle ? bundleUsesCanonicalOcr(bundle, compatibility.compatible) : true;
  const smartOcr = bundle ? bundleUsesSmartOcr(bundle, compatibility.compatible) : false;
  const bundleOcr = ownsOcr ? bundle?.ocr : undefined;
  const bundleRegions = Array.isArray(bundleOcr?.regions) && bundleOcr.regions.length > 0 ? bundleOcr.regions : null;
  const ocrRegions = cloneOcrRegions(bundleRegions ?? active.ocr_regions ?? DEFAULT_VISUAL_LAB_OCR_REGIONS);
  const completedProfileParameters = completeVisualLabParameters(active.detection_parameters);
  const ocrParams = {
    ...completedProfileParameters,
    ...(bundleOcr?.params ?? {}),
  };
  const candidateCnn = (bundle?.candidate_config?.cnn ?? {}) as Record<string, unknown>;
  const candidateOcr = (bundle?.candidate_config?.ocr ?? {}) as Record<string, unknown>;
  const runtime = bundle?.runtime ?? {};
  const cnnEnabled = Boolean(canUseBundleRuntime && bundle?.cnn?.model_id);

  return {
    schemaVersion: 1,
    capturedAt: new Date().toISOString(),
    source: active.source,
    profileId: active.profile?.id ?? null,
    profileName: active.profile?.name ?? null,
    profileStage: active.profile?.stage ?? null,
    profileUpdatedAt: active.profile?.updated_at ?? null,
    profileActivatedAt: active.profile?.activated_at ?? null,
    runtimeApi: compatibility.runtimeApi,
    runtimeCompatible: compatibility.compatible,
    runtimeCompatibilityReason: compatibility.reason,
    bundleId: bundle?.bundle_id ?? null,
    bundleVersion: bundle?.bundle_version ?? null,
    recognitionRevision: Number.isInteger(bundle?.recognition_revision) ? Number(bundle?.recognition_revision) : null,
    bundlePayloadSha256: bundle?.integrity?.bundle_payload_sha256 ?? null,
    candidateConfigSha256: bundle?.candidate_config_sha256 ?? bundle?.integrity?.candidate_config_sha256 ?? null,
    runtimeParameterCatalogVersion: VISION_RUNTIME_PARAMETER_CATALOG_VERSION,
    runtimeParameterCatalogSha256: VISION_RUNTIME_PARAMETER_CATALOG_SHA256,
    pipelineFingerprintVersion: VISION_PIPELINE_FINGERPRINT_VERSION,
    productionPipelineSha256: VISION_PRODUCTION_PIPELINE_SHA256,
    detectionParameters: { ...completedProfileParameters },
    cnn: {
      enabled: cnnEnabled,
      modelId: cnnEnabled ? bundle?.cnn?.model_id ?? null : null,
      modelSha256: cnnEnabled ? bundle?.cnn?.model_sha256 ?? null : null,
      webModelSha256: cnnEnabled ? bundle?.cnn?.web_model_sha256 ?? null : null,
      runtime: cnnEnabled ? String(candidateCnn.runtime ?? "tiny-corner-cnn-js-v2-canonical-input") : null,
      inputTransform: cnnEnabled ? String(candidateCnn.input_transform ?? "canonical-gray-bilinear-half-pixel-v1") : null,
    },
    hybrid: {
      params: canUseBundleRuntime ? { ...(bundle?.hybrid?.params ?? {}) } : {},
      inputScale: canUseBundleRuntime ? Number(bundle?.hybrid?.input_scale ?? 1) : 1,
      confidenceThreshold: canUseBundleRuntime ? Number(bundle?.hybrid?.confidence_threshold ?? 0.85) : 0.85,
      fastPathEnabled: Boolean(canUseBundleRuntime && bundle?.hybrid?.fast_path_enabled),
    },
    ocr: {
      source: ownsOcr ? "bundle" : active.profile ? "profile" : "default",
      params: ocrParams,
      regions: ocrRegions,
      runtime: String(runtime.production_ocr ?? "tesseract.js"),
      validationRuntime: String(candidateOcr.runtime ?? runtime.ocr_validation ?? "") || null,
      preprocessRuntime: bundleOcrPreprocessRuntime(bundle) ?? (canonicalOcr ? "canonical-ocr-js-v1" : null),
      canonicalPreprocess: canonicalOcr,
      smartGridMultiPass: smartOcr,
      languageAssetSha256: {
        deu: VISION_PRODUCTION_COMPONENT_SHA256.tesseract_deu ?? null,
        eng: VISION_PRODUCTION_COMPONENT_SHA256.tesseract_eng ?? null,
      },
    },
    productionBundle: canUseBundleRuntime ? bundle : null,
  };
}

export function visionRuntimeProvenance(snapshot: VisionRuntimeSnapshot, extra?: { fastPathAttempted?: boolean; fastPathFallbackUsed?: boolean; cnnInference?: VisionRuntimeProvenance["cnnInference"] }): VisionRuntimeProvenance {
  const { productionBundle: _productionBundle, ...provenance } = snapshot;
  void _productionBundle;
  return {
    ...provenance,
    detectionParameters: { ...provenance.detectionParameters },
    cnn: { ...provenance.cnn },
    hybrid: { ...provenance.hybrid, params: { ...provenance.hybrid.params } },
    ocr: { ...provenance.ocr, params: { ...provenance.ocr.params }, regions: cloneOcrRegions(provenance.ocr.regions) },
    ...extra,
  };
}

export async function getActiveVisualLabProfile(force = false): Promise<ActiveVisualLabProfile> {
  if (force) {
    try { return await refreshActiveVisualLabProfileFromServer(); }
    catch { return readCachedActiveVisualLabProfile() ?? defaultActiveVisualLabProfile(); }
  }

  const cached = readCachedActiveVisualLabProfile();
  const value = cached ?? defaultActiveVisualLabProfile();
  if (!activeCache) activeCache = { at: Date.now(), value };

  // Local-first: Bildaufnahme/OCR darf niemals auf einen Backend-Timeout warten.
  // Der letzte bekannte Stand wird sofort verwendet und nur best-effort im
  // Hintergrund aktualisiert.
  if (typeof navigator === "undefined" || navigator.onLine) {
    void refreshActiveVisualLabProfileFromServer().catch(() => undefined);
  }
  return value;
}

export function clearActiveVisualLabProfileCache(persistent = false): void {
  activeCache = null;
  if (!persistent) return;
  try {
    localStorage.removeItem(ACTIVE_CACHE_KEY);
    localStorage.removeItem(ACTIVE_CACHE_AT_KEY);
  } catch { /* storage optional */ }
}

export async function refreshActiveVisualLabProfileBestEffort(): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  try {
    await refreshActiveVisualLabProfileFromServer();
    return true;
  } catch {
    return false;
  }
}

export async function reconcileActiveVisualLabProfileAfterProductionChange(
  expectedBundleId: string | null,
): Promise<boolean> {
  // SR28: Server-Aktivierung und lokale Device-Aktivierung sind bewusst zweiphasig.
  // Das bisherige lokale Bundle bleibt aktiv, bis das neue Bundle vollständig geladen,
  // verifiziert und lokal committed wurde. Ein Netz-/Storage-/Hashfehler darf den alten
  // funktionsfähigen Device-Stand nicht vorzeitig löschen.
  try {
    await refreshActiveVisualLabProfileFromServer(expectedBundleId);
    return true;
  } catch {
    return false;
  }
}

export async function listVisualLabProfiles(): Promise<VisualLabProfile[]> {
  return api<VisualLabProfile[]>("/api/admin/visual-lab/profiles");
}

export type VisualLabProfilePayload = { name: string; description?: string | null; change_reason?: string | null; release_notes?: string | null; quality_baseline?: Record<string, number> | null; detection_parameters: Record<string, number>; ocr_regions: VisualLabOcrRegion[] };

export type VisualLabQualitySummary = {
  active_profile_id: number | null;
  total_24h: number;
  found_rate_24h: number | null;
  average_ms_24h: number | null;
  baseline_found_rate: number | null;
  baseline_average_ms: number | null;
  status: "ok" | "warning" | "unknown";
  message: string;
};

export async function createVisualLabProfile(payload: VisualLabProfilePayload): Promise<VisualLabProfile> {
  return api<VisualLabProfile>("/api/admin/visual-lab/profiles", { method: "POST", body: JSON.stringify(payload) });
}

export async function createRecommendedRealLabelProfiles(): Promise<VisualLabProfile[]> {
  const response = await fetchWithTimeout("/api/admin/visual-lab/recommended-real-label-profiles", {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }, 12000);
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json() as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch { /* ignore */ }
    throw new Error(detail);
  }
  return response.json() as Promise<VisualLabProfile[]>;
}

export async function updateVisualLabProfile(profileId: number, payload: VisualLabProfilePayload): Promise<VisualLabProfile> {
  return api<VisualLabProfile>(`/api/admin/visual-lab/profiles/${profileId}`, { method: "PUT", body: JSON.stringify(payload) });
}

export async function duplicateVisualLabProfile(profileId: number): Promise<VisualLabProfile> {
  return api<VisualLabProfile>(`/api/admin/visual-lab/profiles/${profileId}/duplicate`, { method: "POST" });
}

export async function promoteVisualLabProfile(profileId: number, stage: VisualLabStage): Promise<VisualLabProfile> {
  const value = await api<VisualLabProfile>(`/api/admin/visual-lab/profiles/${profileId}/promote`, { method: "POST", body: JSON.stringify({ stage }) });
  if (stage === "production") {
    const converged = await reconcileActiveVisualLabProfileAfterProductionChange(null);
    if (!converged && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("materialentnahme:toast", {
        detail: { message: "Scanprofil wurde serverseitig aktiviert. Der lokale Vision-Stand wird beim nächsten Online-Abgleich aktualisiert." },
      }));
    }
  } else {
    clearActiveVisualLabProfileCache();
  }
  return value;
}

export async function deleteVisualLabProfile(profileId: number): Promise<void> {
  await api<void>(`/api/admin/visual-lab/profiles/${profileId}`, { method: "DELETE" });
}


export async function recordVisualLabQualityEvent(payload: { found: boolean; processing_time_ms: number; confidence?: number | null; method?: string | null }): Promise<void> {
  try {
    await api<void>("/api/visual-lab/quality-event", { method: "POST", body: JSON.stringify(payload) });
  } catch {
    // Quality telemetry must never block the scan workflow.
  }
}

export async function fetchVisualLabQualitySummary(): Promise<VisualLabQualitySummary> {
  return api<VisualLabQualitySummary>("/api/admin/visual-lab/quality-summary");
}
